/**
 * Create Request Page
 * Allows yacht owners to create new mooring assistance requests
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestsAPI, yachtsAPI, portsAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  MapPin,
  Calendar,
  Clock,
  Ship,
  Users,
  ArrowLeft,
  ArrowRight,
  Search,
  CheckCircle,
} from 'lucide-react';
import { format, addDays } from 'date-fns';

function CreateRequest() {
  const navigate = useNavigate();
  const { showToast } = useNotifications();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [yacht, setYacht] = useState(null);
  const [portSearch, setPortSearch] = useState('');
  const [portResults, setPortResults] = useState([]);
  const [searchingPorts, setSearchingPorts] = useState(false);

  const [formData, setFormData] = useState({
    yacht_id: '',
    port_id: '',
    port_name: '',
    scheduled_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    eta_from: '10:00',
    eta_to: '11:00',
    request_type: 'scheduled',
    helpers_needed: 1,
    mooring_type: 'med',
    special_notes: '',
  });

  useEffect(() => {
    loadYacht();
  }, []);

  useEffect(() => {
    const searchPorts = async () => {
      if (portSearch.length < 2) {
        setPortResults([]);
        return;
      }

      setSearchingPorts(true);
      try {
        const data = await portsAPI.search(portSearch);
        setPortResults(data.ports || data.data || []);
      } catch (error) {
        console.error('Port search failed:', error);
      } finally {
        setSearchingPorts(false);
      }
    };

    const debounce = setTimeout(searchPorts, 300);
    return () => clearTimeout(debounce);
  }, [portSearch]);

  const loadYacht = async () => {
    try {
      const data = await yachtsAPI.getMyYachts();
      const yachtsList = data.yachts || data.data || [];
      if (yachtsList.length > 0) {
        setYacht(yachtsList[0]);
        setFormData(prev => ({ ...prev, yacht_id: yachtsList[0].id }));
      }
    } catch (error) {
      console.error('Failed to load yacht:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePortSelect = (port) => {
    setFormData(prev => ({
      ...prev,
      port_id: port.id,
      port_name: port.name,
    }));
    setPortSearch(port.name);
    setPortResults([]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await requestsAPI.create(formData);
      showToast('Request created successfully!', 'success');
      // API returns { success: true, data: { id: ... } }
      const requestId = response.data?.id || response.request?.id || response.id;
      navigate(`/owner/requests/${requestId}`);
    } catch (error) {
      showToast(error.message || 'Failed to create request', 'error');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.port_id && formData.scheduled_date && formData.eta_from && formData.eta_to;
      case 2:
        return formData.helpers_needed > 0;
      default:
        return true;
    }
  };

  // Show loading while fetching yacht
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Request Mooring Help
        </h1>
        <p className="text-navy-600 mt-1">
          Tell us when and where you need assistance
        </p>
      </div>

      {/* Yacht Info Card */}
      {yacht && (
        <div className="card mb-6 bg-gray-50">
          <div className="flex items-center">
            <div className="h-12 w-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
              <Ship className="h-6 w-6 text-navy-400" />
            </div>
            <div className="ml-4">
              <p className="font-medium text-navy-900">{yacht.name}</p>
              <p className="text-sm text-navy-500">
                {yacht.length_m ? `${yacht.length_m}m` : ''} {yacht.yacht_type === 'motor' ? 'Motor Yacht' : yacht.yacht_type === 'sailing' ? 'Sailing Yacht' : yacht.yacht_type}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps - now 2 steps */}
      <div className="flex items-center mb-8">
        {[1, 2].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-full font-medium ${
                step >= s
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-navy-500'
              }`}
            >
              {step > s ? <CheckCircle className="h-5 w-5" /> : s}
            </div>
            {s < 2 && (
              <div
                className={`flex-1 h-1 mx-2 ${
                  step > s ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Location & Time */}
      {step === 1 && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
            When & Where?
          </h2>

          {/* Port Search */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <MapPin className="h-4 w-4 inline mr-1" />
              Port / Marina
            </label>
            <div className="relative">
              <input
                type="text"
                value={portSearch}
                onChange={(e) => {
                  setPortSearch(e.target.value);
                  if (formData.port_id) {
                    setFormData(prev => ({ ...prev, port_id: '', port_name: '' }));
                  }
                }}
                className="input-field pl-10"
                placeholder="Search for a port or marina..."
              />
              <p className="text-xs text-red-500 mt-1">Start typing the port name and select from the dropdown for reliable results</p>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />

              {/* Search Results */}
              {portResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {portResults.map((port) => (
                    <button
                      key={port.id}
                      onClick={() => handlePortSelect(port)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <p className="font-medium text-navy-900">{port.name}</p>
                      <p className="text-sm text-navy-500">{port.city}, {port.country}</p>
                    </button>
                  ))}
                </div>
              )}

              {searchingPorts && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="spinner w-5 h-5" />
                </div>
              )}
            </div>

            {formData.port_id && (
              <p className="mt-2 text-sm text-green-600 flex items-center">
                <CheckCircle className="h-4 w-4 mr-1" />
                {formData.port_name} selected
              </p>
            )}
          </div>

          {/* Date & Time */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Date
            </label>
            <input
              type="date"
              name="scheduled_date"
              value={formData.scheduled_date}
              onChange={handleChange}
              min={format(new Date(), 'yyyy-MM-dd')}
              className="input-field"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Clock className="h-4 w-4 inline mr-1" />
                ETA From
              </label>
              <input
                type="time"
                name="eta_from"
                value={formData.eta_from}
                onChange={handleChange}
                className="input-field"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-2">
                <Clock className="h-4 w-4 inline mr-1" />
                ETA To
              </label>
              <input
                type="time"
                name="eta_to"
                value={formData.eta_to}
                onChange={handleChange}
                className="input-field"
              />
            </div>
          </div>

          {/* Request Type */}
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Request Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, request_type: 'scheduled' }))}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  formData.request_type === 'scheduled'
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Calendar className="h-5 w-5 text-primary-600 mb-2" />
                <p className="font-medium text-navy-900">Scheduled</p>
                <p className="text-sm text-navy-500">Plan ahead</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, request_type: 'real_time' }))}
                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                  formData.request_type === 'real_time'
                    ? 'border-gold-500 bg-gold-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Clock className="h-5 w-5 text-gold-600 mb-2" />
                <p className="font-medium text-navy-900">Real-time</p>
                <p className="text-sm text-navy-500">Need help now</p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Details */}
      {step === 2 && (
        <div className="card">
          <h2 className="text-lg font-display font-semibold text-navy-900 mb-6">
            Additional Details
          </h2>

          {/* Helpers Needed */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Users className="h-4 w-4 inline mr-1" />
              Helpers Needed
            </label>
            <div className="flex items-center space-x-3">
              {[1, 2, 3, 4].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, helpers_needed: num }))}
                  className={`w-12 h-12 rounded-lg font-medium transition-colors ${
                    formData.helpers_needed === num
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-navy-700 hover:bg-gray-200'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Mooring Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Mooring Type
            </label>
            <select
              name="mooring_type"
              value={formData.mooring_type}
              onChange={handleChange}
              className="input-field"
            >
              <option value="med">Mediterranean (stern-to)</option>
              <option value="alongside">Alongside</option>
              <option value="bow">Bow-to</option>
              <option value="anchor">Anchor assistance</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              Additional Notes (optional)
            </label>
            <textarea
              name="special_notes"
              value={formData.special_notes}
              onChange={handleChange}
              rows={4}
              className="input-field"
              placeholder="Any special instructions or requirements..."
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        {step > 1 ? (
          <button
            onClick={() => setStep(step - 1)}
            className="btn-secondary"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
        ) : (
          <div />
        )}

        {step < 2 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed()}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ArrowRight className="h-5 w-5 ml-2" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!canProceed() || loading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center">
                <span className="spinner h-5 w-5 mr-2" />
                Creating...
              </span>
            ) : (
              <>
                Create Request
                <CheckCircle className="h-5 w-5 ml-2" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default CreateRequest;
